import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { compose } from "redux";
import { arrayOf, bool, func, objectOf } from "prop-types";
import Grid from "react-bootstrap/lib/Grid";
import Row from "react-bootstrap/lib/Row";
import Col from "react-bootstrap/lib/Col";
import { NotificationPushConfiguration } from "@ui-kit/components/index.es";
import { actions, selectors } from "reducers/settings";
import { actions as layoutActions } from "reducers/layout";

import Head from "pages/_components/Head";
import I18n from "pages/_components/I18n";
import MainContainer from "pages/_components/MainContainer";
import { resizableRoute } from "pages/_components/Resizable";

import * as i18n from "util/i18n";
import { get, getArray } from "util/config";
import { dispatchV3ShellLayout, isUxDashboardImprovementsEnabled } from "util/v3ShellLayoutSync";
import Notification from "../../_components/Notification";

const TRANSPORT = "push";
const HIDDEN_NOTIFICATION_TYPES = [
    "marketing",
    "tmpLimitApproved",
    "tmpLimitRejected",
    "temporaryLimitApproved",
    "temporaryLimitRejected",
    "ImplLimitApproved",
    "ImplLimitRejected",
];
function QAlertConfiguration({ dispatch, isDesktop, fetching, communicationTypes, configurations, embeddedInV3Shell }) {
    const [newConfiguration, setNewConfiguration] = useState(null);
    const isShellActive = embeddedInV3Shell && isUxDashboardImprovementsEnabled();

    useEffect(() => {
        dispatch(actions.notificationsConfigurationPre(true));
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (!isShellActive) {
            return undefined;
        }
        dispatchV3ShellLayout(dispatch, {
            titleKey: "settings.notificationsConfiguration.qAlert.title",
            goBackPath: "/settings",
            rightContentMobile: null,
            scopes: ["settings"],
        });
        return () => {
            dispatch(layoutActions.setScopes([]));
            dispatch(layoutActions.setTitleKey(""));
            dispatch(layoutActions.setHeaderTitle(""));
            dispatch(layoutActions.setHeaderSubtitle(""));
            dispatch(layoutActions.setGoBackPath(null));
            dispatch(layoutActions.setRightContentMobile(null));
        };
    }, [dispatch, isShellActive]);

    const addItem = (clave, valor) => {
        setNewConfiguration((prevConfig) => ({
            ...prevConfig,
            [clave]: valor,
        }));
    };

    useEffect(() => {
        if (configurations) {
            Object.entries(configurations).forEach(([type, typeConfig]) => {
                let hasSuboptions = false;
                Object.entries(typeConfig?.options).forEach(([optionType, value]) => {
                    hasSuboptions = true;
                    const idSuboption = `${type}.${optionType.toLowerCase()}`;
                    addItem(idSuboption, value);
                });
                if (!hasSuboptions) {
                    addItem(type, typeConfig?.subscribedPush);
                }
            });
        }
        // eslint-disable-next-line
    }, [configurations]);

    const renderTitleMobile = () => (
        <div>
            <h1 className="m-0 align-center">
                <I18n id="settings.notificationsConfiguration.qAlert.title" />
            </h1>
        </div>
    );

    const getSubOptionLabel = (communicationType, option) => {
        const labelSwitchPre = "userInfo.preferences.notificationsConfiguration";
        const defaultLabel = i18n.get(`${labelSwitchPre}.${option.toLowerCase()}.label`);
        return i18n.get(`${labelSwitchPre}.${communicationType}.${option.toLowerCase()}.label`, defaultLabel);
    };

    const getTooltipLabel = (communicationType, option) => {
        const labelTooltipPre = "frontend.notificacionesMobile.tooltip";
        const labelTooltip = `${labelTooltipPre}.${communicationType}.${option.toLowerCase()}`;
        const defaultLabelTooltip = get(`${labelTooltipPre}.${communicationType}`);
        return get(labelTooltip, defaultLabelTooltip);
    };

    const renderOptions = () =>
        communicationTypes
            ?.map((communicationType) => {
                const [key] = Object.entries(communicationType)[0];
                const data = {};
                data.id = key;
                data.label = i18n.get(`communications.communicationsTypes.${key}`);
                const configurationType = configurations[key];
                data.suboptions = Object.entries(configurationType?.options || {}).map(([option]) => {
                    const idSuboption = `${key}.${option.toLowerCase()}`;
                    return {
                        id: idSuboption,
                        label: getSubOptionLabel(key, option),
                        tooltipText: getTooltipLabel(key, option),
                    };
                });

                return data;
            })
            ?.filter((item) => {
                const configuredHiddenTypes = getArray("core.communications.nonConfigurableTypes", []);
                const hiddenTypes = new Set([...HIDDEN_NOTIFICATION_TYPES, ...configuredHiddenTypes]);
                return !hiddenTypes.has(item.id);
            });

    const toggleSwitch = (value, communicationType) => {
        const configurationType = configurations[communicationType];
        if (value === false) {
            let newOptions = {};
            Object.keys(configurationType?.options).forEach((option) => {
                newOptions = { ...newOptions, [option]: false };
            });
            dispatch(actions.modifyNotificationConfigurationOptions(communicationType, false, null, newOptions));
        }
        dispatch(
            actions.modifyNotificationConfigurations(
                communicationType,
                TRANSPORT,
                value,
                `subscribed${TRANSPORT.toUpperCase()}`,
            ),
        );
    };

    const toggleOptionsSwitch = (configuration, key, option, communicationType) => {
        dispatch(
            actions.modifyNotificationConfigurationOptions(communicationType, configuration[key], option.toUpperCase()),
        );
        const enabled = Object.entries(configuration).filter(
            ([optionType, value]) => optionType.includes(`${communicationType}.`) && value,
        );

        // Si no tiene ninguno habilitado
        if (enabled?.length === 0) {
            dispatch(
                actions.modifyNotificationConfigurations(
                    communicationType,
                    TRANSPORT,
                    false,
                    `subscribed${TRANSPORT.toUpperCase()}`,
                ),
            );
            // Si es el primero en habilitar
        } else if (configuration[key] && enabled?.length === 1) {
            dispatch(
                actions.modifyNotificationConfigurations(
                    communicationType,
                    TRANSPORT,
                    true,
                    `subscribed${TRANSPORT.toUpperCase()}`,
                ),
            );
        }
    };

    const handleChange = (configuration, key) => {
        const [option, suboption] = key.split(".");
        if (suboption) {
            addItem(key, configuration[key]);
            toggleOptionsSwitch(configuration, key, suboption, option);
        } else if (configuration[key] !== undefined) {
            addItem(key, configuration[key]);
            toggleSwitch(configuration[key], key);
        } else {
            // disabled subOptions
            setNewConfiguration({ ...configuration });
            toggleSwitch(false, key);
        }
    };

    return (
        <>
            {!isShellActive && <Notification scopeToShow="qAlertConfiguration" />}
            <div className="admin-detail-head pl-0 pr-1">
                {(isDesktop || !isShellActive) && (
                    <Head
                        backLinkTo="/settings"
                        centerElement={!isDesktop && renderTitleMobile}
                        centerContentClassName="mx-5"
                        headerClassName={!isDesktop ? "blue-main-header-mobile" : ""}
                        accessibilityTextId="settings.notificationsConfiguration.qAlert.title"
                    />
                )}
                {isDesktop && (
                    <div className="view-title mb-15">
                        <h1>{i18n.get("settings.notificationsConfiguration.qAlert.title")}</h1>
                    </div>
                )}
            </div>
            <MainContainer showLoader={fetching}>
                <div>
                    <section className="container--layout flex-grow align-items-center pb-5">
                        <Grid className="form-content">
                            <Row className="justify-content-center">
                                <Col sm={12} md={12} lg={6} className="px-0">
                                    {communicationTypes && configurations && newConfiguration ? (
                                        <NotificationPushConfiguration
                                            headText={i18n.get(
                                                "settings.notificationsConfiguration.qAlert.description",
                                            )}
                                            optionsArray={renderOptions()}
                                            configuration={newConfiguration}
                                            onChangeConfiguration={(configuration, key) => {
                                                if (key !== "marketing") {
                                                    handleChange(configuration, key);
                                                }
                                            }}
                                        />
                                    ) : (
                                        undefined
                                    )}
                                </Col>
                            </Row>
                        </Grid>
                    </section>
                </div>
            </MainContainer>
        </>
    );
}

QAlertConfiguration.propTypes = {
    dispatch: func.isRequired,
    fetching: bool.isRequired,
    isDesktop: bool.isRequired,
    communicationTypes: arrayOf(objectOf(bool)).isRequired,
    configurations: arrayOf(objectOf(bool)).isRequired,
    embeddedInV3Shell: bool,
};

QAlertConfiguration.defaultProps = {
    embeddedInV3Shell: false,
};

const mapStateToProps = (state) => ({
    communicationTypes: selectors.getCommunicationTypes(state),
    configurations: selectors.getConfigurations(state),
    fetching: selectors.isFetching(state),
});

export default compose(connect(mapStateToProps), resizableRoute)(QAlertConfiguration);
